import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, FileText, Code, Link2, ArrowRight, ArrowLeft, AlertTriangle, Copy, Check, ListTree, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import type { VisualSeed, VisualNode } from "@/hooks/useMapVisualData";
import { ROLE_COLORS, ROLE_LABELS } from "./constants";

interface ArticleListProps {
  seeds: VisualSeed[];
  nodes: VisualNode[];
}

export function ArticleList({ seeds, nodes }: ArticleListProps) {
  const [previewNode, setPreviewNode] = useState<VisualNode | null>(null);
  const [copied, setCopied] = useState(false);
  const [sortBy, setSortBy] = useState<"qa" | "words" | "title">("qa");

  const readyNodes = nodes.filter(n =>
    n.final_html && n.final_html.length > 100 &&
    ["ready", "published"].includes(n.engine_stage || "")
  );

  const sorted = [...readyNodes].sort((a, b) => {
    if (sortBy === "qa") return (b.quality_score || 0) - (a.quality_score || 0);
    if (sortBy === "words") return (b.word_count || 0) - (a.word_count || 0);
    return a.title.localeCompare(b.title);
  });

  const seedNameMap = new Map(seeds.map(s => [s.id, s.name]));

  if (readyNodes.length === 0) {
    return (
      <div className="border-2 border-dashed rounded-xl p-12 text-center">
        <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
        <p className="text-muted-foreground">Brak gotowych artykulow — wygeneruj tresci w Content Engine</p>
      </div>
    );
  }

  const handleCopy = async (html: string) => {
    await navigator.clipboard.writeText(html);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {/* Sort controls */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-muted-foreground">Sortuj:</span>
        {(["qa", "words", "title"] as const).map(s => (
          <Badge
            key={s}
            variant={sortBy === s ? "default" : "outline"}
            className="text-[10px] cursor-pointer px-1.5 py-0"
            onClick={() => setSortBy(s)}
          >
            {s === "qa" ? "QA Score" : s === "words" ? "Slowa" : "Tytul"}
          </Badge>
        ))}
        <span className="text-xs text-muted-foreground ml-auto">{readyNodes.length} artykulow</span>
      </div>

      {/* Article list */}
      <div className="space-y-2">
        {sorted.map(node => (
          <div key={node.id} className="rounded-lg border bg-card hover:border-green-500/30 transition-colors p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm truncate">{node.title}</h3>
              <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                <span>{seedNameMap.get(node.seed_id) || "?"}</span>
                {node.document_role === "pillar" ? (
                  <Badge
                    variant="default"
                    className="text-[9px] px-1 py-0 bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-500/50 gap-0.5"
                    title="Hub klastra (pillar). Generowany w SRO → Pillar Page."
                  >
                    <Layers className="h-2.5 w-2.5" />
                    PILLAR
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px] px-1 py-0" style={{ borderColor: ROLE_COLORS[node.document_role], color: ROLE_COLORS[node.document_role] }}>
                    {ROLE_LABELS[node.document_role] || node.document_role}
                  </Badge>
                )}
                {node.word_count && <span>{node.word_count} slow</span>}
                {node.quality_score != null && (
                  <span className={node.quality_score >= 70 ? "text-green-400" : node.quality_score >= 40 ? "text-amber-400" : "text-red-400"}>
                    QA: {node.quality_score}/100
                  </span>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={() => setPreviewNode(node)}>
              <Eye className="h-3 w-3 mr-1" /> Podglad
            </Button>
          </div>
        ))}
      </div>

      {/* Preview dialog with tabs */}
      {previewNode && (
        <ArticlePreviewModal
          node={previewNode}
          onClose={() => setPreviewNode(null)}
        />
      )}
    </>
  );
}

// =============================================
// Article Preview Modal — 4 taby: Podglad / HTML / Struktura / Linki
// =============================================

function ArticlePreviewModal({ node, onClose }: { node: VisualNode; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [incomingLinks, setIncomingLinks] = useState<Array<{ title: string; slug: string; relation_type: string }>>([]);

  const html = node.final_html || "";

  // Extract headings
  const headings = useMemo(() => {
    const result: Array<{ level: number; text: string }> = [];
    const regex = /<h([23])[^>]*>(.*?)<\/h\1>/gi;
    let m;
    while ((m = regex.exec(html)) !== null) {
      result.push({ level: parseInt(m[1]), text: m[2].replace(/<[^>]+>/g, "") });
    }
    return result;
  }, [html]);

  // Extract outgoing links — matchuje OBOJE (relative + absolute do tej samej domeny).
  // Po Link Integrity strip pre-publish większość internal jest absolute URL.
  // Internal heurystyka: single-segment path (typowy WP postname permalink).
  const outgoingLinks = useMemo(() => {
    const pairs: Array<[string, { slug: string; anchor: string }]> = [];
    const matches = Array.from(
      html.matchAll(/<a[^>]*href="([^"]+)"[^>]*>([^<]*)<\/a>/g),
    );
    for (const m of matches) {
      const raw = m[1];
      const anchor = m[2];
      // Zewnetrzne zrodla maja rel="...nofollow", internal NIGDY — bez tego filtra
      // zrodlo z jednosegmentowa sciezka wpadalo na liste "Linkuje do".
      if (/rel="[^"]*nofollow[^"]*"/.test(m[0])) continue;
      let path = "";
      if (raw.startsWith("/")) {
        path = raw;
      } else if (/^https?:\/\//.test(raw)) {
        try {
          path = new URL(raw).pathname;
        } catch {
          continue;
        }
      } else {
        continue;
      }
      if (!/^\/[^/]+\/?$/.test(path)) continue;
      pairs.push([path, { slug: path, anchor }]);
    }
    return [...new Map(pairs).values()];
  }, [html]);

  // Fetch incoming links from DB
  useEffect(() => {
    (async () => {
      const { data: rels } = await (supabase as any)
        .from("thematic_node_relations")
        .select("from_node_id, relation_type")
        .eq("to_node_id", node.id);
      if (!rels || rels.length === 0) { setIncomingLinks([]); return; }
      const fromIds = rels.map((r: any) => r.from_node_id);
      const { data: fromNodes } = await (supabase as any)
        .from("thematic_nodes")
        .select("id, title, slug")
        .in("id", fromIds);
      const nodeMap = new Map((fromNodes || []).map((n: any) => [n.id, n]));
      setIncomingLinks(rels.map((r: any) => {
        const n = nodeMap.get(r.from_node_id);
        return { title: n?.title || "?", slug: n?.slug || "?", relation_type: r.relation_type };
      }));
    })();
  }, [node.id]);

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const wordCount = html.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  const charCount = html.replace(/<[^>]+>/g, "").length;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base truncate pr-4">{node.title}</DialogTitle>
            <div className="flex items-center gap-2 shrink-0">
              {node.quality_score != null && (
                <Badge variant="outline" className="text-xs">QA: {node.quality_score}/100</Badge>
              )}
              <Badge variant="outline" className="text-xs">{wordCount} slow / {charCount} zn.</Badge>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="preview" className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="preview" className="gap-1.5"><Eye className="h-3.5 w-3.5" /> Podglad</TabsTrigger>
              <TabsTrigger value="html" className="gap-1.5"><Code className="h-3.5 w-3.5" /> HTML</TabsTrigger>
              <TabsTrigger value="structure" className="gap-1.5"><ListTree className="h-3.5 w-3.5" /> Struktura</TabsTrigger>
              <TabsTrigger value="links" className="gap-1.5"><Link2 className="h-3.5 w-3.5" /> Linki ({outgoingLinks.length + incomingLinks.length})</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleCopy(html.replace(/<[^>]+>/g, "\n").replace(/\n{3,}/g, "\n\n").trim())}>
                {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                Tekst
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleCopy(html)}>
                <Code className="h-3 w-3 mr-1" /> HTML
              </Button>
            </div>
          </div>

          {/* Preview */}
          <TabsContent value="preview" className="flex-1 overflow-y-auto mt-3">
            <div
              className="prose prose-sm dark:prose-invert max-w-none px-6 py-4 border rounded-lg bg-background
                prose-headings:font-bold prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3 prose-h2:text-violet-700 dark:prose-h2:text-violet-400
                prose-h3:text-base prose-h3:mt-4 prose-h3:mb-2 prose-h3:text-blue-700 dark:prose-h3:text-blue-400
                prose-p:my-2 prose-li:my-0.5 prose-strong:text-foreground
                prose-blockquote:border-l-violet-500 prose-blockquote:bg-muted/30 prose-blockquote:py-1 prose-blockquote:px-4"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </TabsContent>

          {/* HTML source */}
          <TabsContent value="html" className="flex-1 overflow-hidden mt-3">
            <textarea
              value={html}
              readOnly
              className="w-full h-full min-h-[400px] p-4 font-mono text-xs bg-muted/30 border rounded-lg resize-none focus:outline-none"
              spellCheck={false}
            />
          </TabsContent>

          {/* Structure */}
          <TabsContent value="structure" className="flex-1 overflow-y-auto mt-3">
            <div className="px-4 py-3 border rounded-lg space-y-1">
              {headings.length === 0 ? (
                <p className="text-sm text-muted-foreground">Brak naglowkow</p>
              ) : (
                headings.map((h, i) => (
                  <div key={i} className={`flex items-center gap-2 ${h.level === 3 ? "ml-6" : ""}`}>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      h.level === 2 ? "bg-violet-500/20 text-violet-300" : "bg-blue-500/15 text-blue-400"
                    }`}>H{h.level}</span>
                    <span className={`text-sm ${h.level === 2 ? "font-medium" : "text-muted-foreground"}`}>{h.text}</span>
                  </div>
                ))
              )}
              <div className="pt-3 mt-3 border-t flex items-center gap-4 text-xs text-muted-foreground">
                <span>{headings.filter(h => h.level === 2).length} H2</span>
                <span>{headings.filter(h => h.level === 3).length} H3</span>
                <span>{wordCount} slow</span>
              </div>
            </div>
          </TabsContent>

          {/* Links */}
          <TabsContent value="links" className="flex-1 overflow-y-auto mt-3">
            <div className="px-4 py-3 border rounded-lg space-y-4">
              <div>
                <p className="text-xs font-medium text-blue-400 mb-2 flex items-center gap-1.5">
                  <ArrowRight className="h-3.5 w-3.5" /> Linkuje do ({outgoingLinks.length})
                </p>
                {outgoingLinks.length === 0 ? (
                  <p className="text-xs text-muted-foreground ml-5">Brak linkow wewnetrznych w tekscie</p>
                ) : (
                  <div className="space-y-1 ml-5">
                    {outgoingLinks.map((link, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-xs text-muted-foreground font-mono">{link.slug}</span>
                        <span className="text-muted-foreground">—</span>
                        <span>{link.anchor}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-green-400 mb-2 flex items-center gap-1.5">
                  <ArrowLeft className="h-3.5 w-3.5" /> Linkuja tutaj ({incomingLinks.length})
                </p>
                {incomingLinks.length === 0 ? (
                  <p className="text-xs text-muted-foreground ml-5">Zaden artykul nie linkuje tutaj</p>
                ) : (
                  <div className="space-y-1 ml-5">
                    {incomingLinks.map((link, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="font-medium">{link.title}</span>
                        <span className="text-xs text-muted-foreground">({link.relation_type})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {outgoingLinks.length < 2 && (
                <div className="flex items-center gap-2 text-xs text-yellow-500">
                  <AlertTriangle className="h-3.5 w-3.5" /> Mniej niz 2 linki wychodzace
                </div>
              )}
              {incomingLinks.length === 0 && (
                <div className="flex items-center gap-2 text-xs text-yellow-500">
                  <AlertTriangle className="h-3.5 w-3.5" /> Izolowany node — zaden artykul nie linkuje tutaj
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
